<template>
    <div
        v-resize:debounce="onResize"
        class="listing-card-container"
        :class="[normalizedOptions.fillHeight ? 'fill-height' : '']"
    >
        <v-card
            tile
            class="listing-card mx-auto"
            :class="[
                normalizedOptions.landscape && !resMobile
                    ? 'listing-card-landscape my-3'
                    : 'my-6 listing-card-portrait',
                listing.featured ? 'listing-card-featured' : '',
                listing.displayAs == 'premium'
                    ? 'listing-card-highlighted'
                    : '',
            ]"
            elevation="3"
            :height="normalizedOptions.fillHeight ? '100%' : 'auto'"
        >
            <v-row align-items="stretch">
                <v-col
                    :cols="normalizedOptions.landscape && !resMobile ? 6 : 12"
                >
                    <div
                        class="hp"
                        v-if="
                            normalizedOptions.hideLogos === false &&
                            listing.logo &&
                            (resMobile || !normalizedOptions.landscape)
                        "
                    >
                        <img
                            :src="listing.logo.url"
                            :alt="
                                listing.logo.caption
                                    ? listing.logo.caption
                                    : 'Brand Logo'
                            "
                        />
                    </div>

                    <div class="card-header">
                        <div
                            :class="secondaryLabel.classes"
                            :style="
                                normalizedOptions.labels.secondaryLabel.style
                            "
                        >
                            <span>
                                {{ secondaryLabel.text }}
                            </span>
                        </div>

                        <v-chip label small :class="primaryLabel.classes">
                            {{ primaryLabel.text }}
                        </v-chip>

                        <v-carousel
                            v-if="
                                normalizedOptions.thumbnailLimit > 1 &&
                                listingImages.length > 1
                            "
                            hide-delimiters
                        >
                            <v-carousel-item
                                v-for="(image, index) in listingImages"
                                :key="index"
                            >
                                <a
                                    :href="linkToListing"
                                    @click="
                                        sendMessageToParent(
                                            'urlChangedTo',
                                            linkToListing
                                        )
                                    "
                                >
                                    <v-img
                                        class="listing-card-thumbnail"
                                        :src="image.thumbnail"
                                        :alt="image.alternativeText"
                                    ></v-img>
                                </a>
                            </v-carousel-item>
                        </v-carousel>

                        <a
                            v-else
                            :href="linkToListing"
                            @click="
                                sendMessageToParent(
                                    'urlChangedTo',
                                    linkToListing
                                )
                            "
                        >
                            <v-img
                                class="listing-card-thumbnail"
                                :src="listingImage"
                            ></v-img>
                        </a>
                    </div>
                </v-col>

                <v-col
                    :cols="normalizedOptions.landscape && !resMobile ? 6 : 12"
                    class="card-body-outer"
                >
                    <a
                        :href="linkToListing"
                        @click="
                            sendMessageToParent('urlChangedTo', linkToListing)
                        "
                    ></a>
                    <div
                        class="hp"
                        v-if="
                            normalizedOptions.hideLogos === false &&
                            listing.logo &&
                            normalizedOptions.landscape &&
                            !resMobile
                        "
                    >
                        <img
                            :src="listing.logo.url"
                            :alt="
                                listing.logo.caption
                                    ? listing.logo.caption
                                    : 'Brand Logo'
                            "
                        />
                    </div>

                    <div
                        class="card-body"
                        :class="
                            normalizedOptions.hideLogos === false &&
                            listing.logo &&
                            ((normalizedOptions.landscape && !resMobile) ||
                                normalizedOptions.landscape)
                                ? ''
                                : 'mt-4'
                        "
                    >
                        <v-row
                            class="flex-grow-0"
                            v-if="showLinkToListing && linkToListing"
                        >
                            <v-col
                                v-if="normalizedOptions.propertyTypeIcons"
                                cols="12"
                                class="text-right mt-3"
                            >
                                <span v-if="hasFlat">
                                    <font-awesome-icon
                                        v-if="
                                            normalizedOptions.iconLibrary ==
                                            'fontawesome'
                                        "
                                        icon="fa-solid fa-building"
                                        class="pl-1"
                                        :class="calculatedIconColour"
                                    />
                                    <v-icon
                                        v-else
                                        class="pl-1"
                                        :class="calculatedIconColour"
                                        >mdi-office-building</v-icon
                                    >
                                </span>

                                <span v-if="hasHouse">
                                    <font-awesome-icon
                                        v-if="
                                            normalizedOptions.iconLibrary ==
                                            'fontawesome'
                                        "
                                        icon="fa-solid fa-home"
                                        class="pl-1"
                                        :class="calculatedIconColour"
                                    />
                                    <v-icon
                                        v-else
                                        class="pl-1"
                                        :class="calculatedIconColour"
                                        >mdi-home</v-icon
                                    >
                                </span>

                                <span v-if="wheelchairAccessible">
                                    <font-awesome-icon
                                        v-if="
                                            normalizedOptions.iconLibrary ==
                                            'fontawesome'
                                        "
                                        icon="fa-solid fa-wheelchair-move"
                                        class="pl-1"
                                        :class="calculatedIconColour"
                                    />
                                    <v-icon
                                        v-else
                                        class="pl-1"
                                        :class="calculatedIconColour"
                                        >mdi-wheelchair-accessibility</v-icon
                                    >
                                </span>
                            </v-col>

                            <v-col cols="12">
                                <v-card-title
                                    class="mb-2 pb-0 px-0 fs-1125 letter-spacing-normal"
                                    :class="calculatedTitleColour"
                                    >{{ listing.name }}</v-card-title
                                >
                            </v-col>
                        </v-row>

                        <v-card-title
                            class="mb-2 pb-0 px-0 fs-1125 letter-spacing-normal"
                            v-else
                        >
                            {{ listing.name }}
                        </v-card-title>

                        <v-row class="flex-grow-0 mb-4">
                            <v-col cols="8">
                                <v-card-text
                                    class="listing-address py-0 px-0 fs-0875 letter-spacing-normal"
                                    >{{ listing.displayAddress }},
                                    {{ listing.postcode }}</v-card-text
                                >
                            </v-col>
                            <v-col
                                v-if="normalizedOptions.quantityIcons"
                                cols="4"
                                class="quantity-icons text-right"
                            >
                                <span class="icon pr-2">
                                    <font-awesome-icon
                                        v-if="
                                            normalizedOptions.iconLibrary ==
                                            'fontawesome'
                                        "
                                        icon="fa-solid fa-bed"
                                        :class="calculatedIconColour"
                                    />
                                    <v-icon
                                        v-else
                                        class="pr-1"
                                        :class="calculatedIconColour"
                                        >mdi-bed-king-outline</v-icon
                                    >
                                    <span class="font-weight-bold fs-075">{{
                                        listingBedrooms
                                    }}</span>
                                </span>

                                <span class="icon">
                                    <font-awesome-icon
                                        v-if="
                                            normalizedOptions.iconLibrary ==
                                            'fontawesome'
                                        "
                                        icon="fa-solid fa-shower"
                                        :class="calculatedIconColour"
                                    />
                                    <v-icon
                                        v-else
                                        class="pr-1"
                                        :class="calculatedIconColour"
                                        >mdi-shower-head</v-icon
                                    >
                                    <span class="font-weight-bold fs-075">{{
                                        listingBathrooms
                                    }}</span>
                                </span>
                            </v-col>
                        </v-row>

                        <v-card-text class="px-0" v-if="listing.forSale">
                            <v-row
                                v-if="
                                    normalizedOptions.stats.availability &&
                                    normalizedOptions.stats.availability.shown
                                "
                            >
                                <v-col class="pt-0 pb-1" cols="6">{{
                                    normalizedOptions.stats.availability
                                        .label || "Availability"
                                }}</v-col>
                                <v-col
                                    cols="6"
                                    class="listing-availability pt-0 pb-1 text-right font-weight-bold"
                                    >{{ listingAvailability }}</v-col
                                >
                            </v-row>
                            <v-row
                                v-if="
                                    !normalizedOptions.quantityIcons &&
                                    normalizedOptions.stats.bedrooms.shown
                                "
                            >
                                <v-col class="pt-0 pb-1" cols="6">{{
                                    normalizedOptions.stats.bedrooms.label ||
                                    "Bedrooms"
                                }}</v-col>
                                <v-col
                                    cols="6"
                                    class="listing-bedrooms pt-0 pb-1 text-right font-weight-bold"
                                    >{{ listingBedrooms }}</v-col
                                >
                            </v-row>
                            <v-row
                                v-if="
                                    !normalizedOptions.quantityIcons &&
                                    normalizedOptions.stats.bathrooms.shown
                                "
                            >
                                <v-col class="pt-0 pb-1" cols="6">{{
                                    normalizedOptions.stats.bathrooms.label ||
                                    "Bathrooms"
                                }}</v-col>
                                <v-col
                                    cols="6"
                                    class="listing-bathrooms pt-0 pb-1 text-right font-weight-bold"
                                    >{{ listingBathrooms }}</v-col
                                >
                            </v-row>
                            <v-row
                                v-if="showListingRow('deposit', 'depositValue')"
                            >
                                <v-col class="pt-0 pb-1" cols="6">{{
                                    normalizedOptions.stats.deposit.label ||
                                    "Minimum deposit"
                                }}</v-col>
                                <v-col
                                    cols="6"
                                    class="listing-deposit pt-0 pb-1 text-right font-weight-bold"
                                    >{{ listingDeposit }}</v-col
                                >
                            </v-row>
                            <v-row
                                class="minShare"
                                v-if="
                                    showListingRow(
                                        'sharePercentage',
                                        'minSharePercentage'
                                    ) && listing.isSharedOwnership
                                "
                            >
                                <v-col
                                    class="listing-share-percentage label pt-0 pb-1"
                                    cols="6"
                                    >{{
                                        normalizedOptions.stats.sharePercentage
                                            .label || "Min.Share"
                                    }}</v-col
                                >
                                <v-col
                                    cols="6"
                                    class="listing-share-percentage pt-0 pb-1 text-right font-weight-bold"
                                    >{{ listingMinSharePercentage }}</v-col
                                >
                            </v-row>
                            <v-row
                                v-if="
                                    showListingRow('share', 'minShareValue') &&
                                    listing.isSharedOwnership
                                "
                            >
                                <v-col
                                    class="listing-share-percentage pt-0 pb-1"
                                    cols="6"
                                >
                                    <span v-if="listing.minSharePercentage"
                                        >{{ listing.minSharePercentage }}%</span
                                    >
                                    Share from
                                </v-col>
                                <v-col
                                    cols="6"
                                    class="listing-share pt-0 pb-1 text-right font-weight-bold"
                                    >{{ listingMinShareValue }}</v-col
                                >
                            </v-row>
                            <v-row
                                v-if="
                                    showListingRow('price', 'fullMarketPrice')
                                "
                            >
                                <v-col class="pt-0 pb-1" cols="6">{{
                                    normalizedOptions.stats.price.label ||
                                    "Full price"
                                }}</v-col>
                                <v-col
                                    cols="6"
                                    class="listing-value pt-0 pb-1 text-right fs-1125 font-weight-bold"
                                    >{{ listingFullValue }}</v-col
                                >
                            </v-row>
                        </v-card-text>
                        <v-card-text
                            class="px-0"
                            v-if="
                                !listing.forSale &&
                                normalizedOptions.stats.rent.shown
                            "
                        >
                            <v-row
                                v-if="
                                    normalizedOptions.stats.availability &&
                                    normalizedOptions.stats.availability.shown
                                "
                            >
                                <v-col class="pt-0 pb-1" cols="6">{{
                                    normalizedOptions.stats.availability
                                        .label || "Availability"
                                }}</v-col>
                                <v-col
                                    cols="6"
                                    class="listing-availability pt-0 pb-1 text-right font-weight-bold"
                                    >{{ listingAvailability }}</v-col
                                >
                            </v-row>
                            <template
                                v-if="
                                    has(
                                        listing,
                                        'widgetSiteSettings.customPrices'
                                    ) &&
                                    listing.widgetSiteSettings.customPrices
                                        .length > 0
                                "
                            >
                                <v-row
                                    v-for="customPrice in listing
                                        .widgetSiteSettings.customPrices"
                                    :key="customPrice.id"
                                >
                                    <v-col class="pt-0 pb-1" cols="6">{{
                                        customPrice.label
                                    }}</v-col>
                                    <v-col
                                        cols="6"
                                        class="pt-0 pb-1 text-right font-weight-bold"
                                        >{{
                                            currencyFormatter.format(
                                                customPrice.value
                                            )
                                        }}</v-col
                                    >
                                </v-row>
                            </template>
                            <template v-else>
                                <v-row v-if="listing.weeklyRent">
                                    <v-col class="pt-0 pb-1" cols="6"
                                        >Weekly rent</v-col
                                    >
                                    <v-col
                                        cols="6"
                                        class="listing-weekly-rent pt-0 pb-1 text-right font-weight-bold"
                                        >{{
                                            currencyFormatter.format(
                                                listing.weeklyRent
                                            )
                                        }}</v-col
                                    >
                                </v-row>
                                <v-row v-if="listing.monthlyRent">
                                    <v-col class="pt-0 pb-1" cols="6"
                                        >Monthly rent</v-col
                                    >
                                    <v-col
                                        cols="6"
                                        class="listing-monthly-rent pt-0 pb-1 text-right font-weight-bold fs-1125"
                                        >{{
                                            currencyFormatter.format(
                                                listing.monthlyRent
                                            )
                                        }}</v-col
                                    >
                                </v-row>
                            </template>
                        </v-card-text>
                    </div>
                </v-col>
            </v-row>
        </v-card>
    </div>
</template>

<script>
import {
    defineComponent,
    ref,
    computed,
    onMounted,
} from "@vue/composition-api";
import { has, merge } from "lodash";
import isMobile from "../composables/isMobile";

export default defineComponent({
    name: "ListingCard",
    components: {},
    props: {
        listing: {
            type: Object,
            required: true,
        },
        showLinkToListing: {
            type: Boolean,
            default: false,
        },
        linkToListing: {
            type: String,
        },
        linkCard: {
            type: Boolean,
            default: false,
        },
        options: {
            type: Object,
        },
    },
    setup(props, context) {
        const resMobile = ref(false);

        const normalizedOptions = computed(() => {
            return Object.assign(
                {
                    fillHeight: false,
                    landscape: false,
                    iconLibrary: null,
                    quantityIcons: true,
                    propertyTypeIcons: false,
                    hideLogos: false,
                    thumbnailLimit: 1,
                    stats: {
                        availability: { shown: true, label: "Availability" },
                        bedrooms: { shown: false, label: "Bedrooms" },
                        bathrooms: { shown: false, label: "Bathrooms" },
                        deposit: {
                            shown: true,
                            showWhenNull: false,
                            label: "Minimum deposit",
                        },
                        sharePercentage: {
                            shown: true,
                            showWhenNull: false,
                            label: "Min. Share",
                        },
                        share: { shown: true, showWhenNull: false },
                        price: {
                            shown: true,
                            showWhenNull: false,
                            label: "Full price",
                            field: "fullMarketPrice",
                        },
                        rent: { shown: true, showWhenNull: false },
                    },
                    labels: {
                        scheme: {
                            classes: "",
                            style: (props.listing.schemes.length > 0)
                                ? "background-color: " + props.listing.schemes[0].color : '',
                        },
                        newBuild: {
                            classes: "",
                            style: "",
                        },
                        resale: {
                            classes: "",
                            style: "",
                        },
                        secondaryLabel: {
                            classes: "",
                            style: "",
                        },
                    },
                },
                props.options
            );
        });

        const hasFlat = computed(() => {
            if (
                props.listing.availablePropertyTypes &&
                props.listing.availablePropertyTypes.length > 0
            ) {
                return props.listing.availablePropertyTypes.includes("Flat");
            } else {
                return props.listing.propertyType == "Flat";
            }
        });

        const hasHouse = computed(() => {
            if (
                props.listing.availablePropertyTypes &&
                props.listing.availablePropertyTypes.length > 0
            ) {
                return (
                    props.listing.availablePropertyTypes.filter(
                        (val) => val != "Flat"
                    ).length > 0
                );
            } else {
                return props.listing.propertyType != "Flat";
            }
        });

        const calculatedTitleColour = computed(() => {
            if (props.listing.featured) {
                return "white--text";
            }

            return "grey--text text--darken-3";
        });

        const calculatedIconColour = computed(() => {
            if (props.listing.featured) {
                return "white--text";
            }

            return "grey--text text--darken-1";
        });

        const wheelchairAccessible = computed(() => {
            let isWheelchairAccessible = false;

            if (!props.listing.propertyFeatures) {
                return false;
            }

            props.listing.propertyFeatures.forEach((feature) => {
                if (feature.values[0] === "Wheelchair accessible") {
                    isWheelchairAccessible = true;
                }
            });

            return isWheelchairAccessible;
        });

        const currencyFormatter = new Intl.NumberFormat("gb-GB", {
            style: "currency",
            currency: "GBP",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        });

        const listingImage = computed(() => {
            if (_.has(props.listing.mainImage, "thumbnail")) {
                return props.listing.mainImage.thumbnail;
            } else if (_.has(props.listing, "galleryImages")) {
                let mainGalleryImage = _.find(
                    props.listing.galleryImages,
                    function (o) {
                        return o.main === true;
                    }
                );
                if (mainGalleryImage) {
                    return mainGalleryImage.thumbnail;
                }
                return false;
            }
            return "";
        });

        const listingImages = computed(() => {
            if (has(props.listing, "galleryImages")) {
                return props.listing.galleryImages.slice(
                    0,
                    normalizedOptions.value.thumbnailLimit
                );
            }
            return [];
        });

        const listingAvailability = computed(() => {
            if (has(props.listing, "availabilityStatus.name")) {
                return props.listing.availabilityStatus.name;
            } else {
                return "TBC";
            }
        });

        const listingBedrooms = computed(() => {
            return props.listing.bedroomsFormatted != ""
                ? props.listing.bedroomsFormatted.replace("0", "Studio")
                : "TBC";
        });

        const listingBathrooms = computed(() => {
            return props.listing.bathroomsFormatted != ""
                ? props.listing.bathroomsFormatted
                : "TBC";
        });

        const listingDeposit = computed(() => {
            if (
                props.listing.availabilityStatus.code != "coming-soon" &&
                props.listing.depositValue > 0
            ) {
                return currencyFormatter.format(props.listing.depositValue);
            }
            return "TBC";
        });

        const listingMinSharePercentage = computed(() => {
            if (
                props.listing.availabilityStatus.code != "coming-soon" &&
                props.listing.minSharePercentage > 0
            ) {
                return props.listing.minSharePercentage + "%";
            }
            return "TBC";
        });

        const listingMinShareValue = computed(() => {
            if (
                props.listing.availabilityStatus.code != "coming-soon" &&
                props.listing.minShareValue > 0
            ) {
                return currencyFormatter.format(props.listing.minShareValue);
            }
            return "TBC";
        });

        const listingFullValue = computed(() => {
            if (props.listing.availabilityStatus.code == "coming-soon") {
                return "TBC";
            } else if (has(normalizedOptions.value, "stats.price.field")) {
                const fieldToShow = normalizedOptions.value.stats.price.field;
                if (has(props, "listing." + fieldToShow)) {
                    return props.listing[fieldToShow]
                        ? currencyFormatter.format(props.listing[fieldToShow])
                        : "TBC";
                } else {
                    return props.listing.fullMarketPrice
                        ? currencyFormatter.format(
                              props.listing.fullMarketPrice
                          )
                        : "TBC";
                }
            } else {
                return props.listing.fullMarketPrice
                    ? currencyFormatter.format(props.listing.fullMarketPrice)
                    : "TBC";
            }
        });

        const primaryLabel = computed(() => {
            let primaryLabelReturn = {
                text: "",
                classes: "type-ribbon",
            };
            if (
                has(props, "listing.widgetSiteSettings.primaryLabel") &&
                props.listing.widgetSiteSettings.primaryLabel
            ) {
                primaryLabelReturn = {
                    text: props.listing.widgetSiteSettings.primaryLabel,
                    classes: "type-ribbon",
                };
            } else if (props.listing.schemeGroup.forSale === false) {
                primaryLabelReturn = {
                    text: "Rental",
                    classes: "type-ribbon rental",
                };
            } else if (props.listing.newBuild) {
                primaryLabelReturn = {
                    text: "New home",
                    classes:
                        normalizedOptions.value.labels.newBuild.classes +
                        " type-ribbon",
                };
            } else {
                primaryLabelReturn = {
                    text: "Resale",
                    classes:
                        normalizedOptions.value.labels.resale.classes +
                        " type-ribbon resale",
                };
            }

            return primaryLabelReturn;
        });

        const secondaryLabel = computed(() => {
            let secondaryLabelReturn = {
                classes: normalizedOptions.value.labels.secondaryLabel.classes,
                text: "",
            };
            if (
                has(props, "listing.widgetSiteSettings.secondaryLabel") &&
                props.listing.widgetSiteSettings.secondaryLabel
            ) {
                secondaryLabelReturn = {
                    classes:
                        normalizedOptions.value.labels.secondaryLabel.classes +
                        " secondary-label",
                    text: props.listing.widgetSiteSettings.secondaryLabel,
                };
            } else if (
                props.listing.schemeGroup &&
                props.listing.schemeGroup.type == "custom"
            ) {
                secondaryLabelReturn = {
                    classes:
                        normalizedOptions.value.labels.secondaryLabel.classes +
                        " scheme-ribbon",
                    text: props.listing.schemeGroup.name,
                };
            } else if (
                props.listing.schemes &&
                props.listing.schemes.length > 0
            ) {
                secondaryLabelReturn = {
                    classes:
                        normalizedOptions.value.labels.secondaryLabel.classes +
                        " scheme-ribbon",
                    text: props.listing.schemes[0].text,
                };
            }
            return secondaryLabelReturn;
        });

        function showListingRow(rowType, listingFieldName) {
            if (has(normalizedOptions.value, "stats." + rowType + ".shown")) {
                if (normalizedOptions.value.stats[rowType].shown) {
                    if (
                        has(
                            normalizedOptions.value,
                            "stats." + rowType + ".showWhenNull"
                        )
                    ) {
                        if (
                            normalizedOptions.value.stats[rowType].showWhenNull
                        ) {
                            return true;
                        } else {
                            if (props.listing[listingFieldName]) {
                                return true;
                            }
                        }
                    } else {
                        if (props.listing[listingFieldName]) {
                            return true;
                        }
                    }
                }
            }
            return false;
        }

        function sendMessageToParent(messageType, messageValue) {
            let message = {
                type: messageType,
                value: messageValue,
            };
            window.top.postMessage(message, "*");
        }

        function onResize() {
            if (!isMobile()) {
                resMobile.value = false;
            } else {
                resMobile.value = true;
            }
        }

        onMounted(() => {
            onResize();
        });

        return {
            wheelchairAccessible,
            calculatedTitleColour,
            calculatedIconColour,
            normalizedOptions,
            currencyFormatter,
            listingImage,
            listingImages,
            sendMessageToParent,
            onResize,
            showListingRow,
            resMobile,

            primaryLabel,
            secondaryLabel,

            listingBedrooms,
            listingBathrooms,
            listingMinSharePercentage,
            listingMinShareValue,
            listingDeposit,
            listingFullValue,

            hasHouse,
            hasFlat,
            listingAvailability,
            has,
        };
    },
});
</script>
<style lang="scss">
@import "~vuetify/src/styles/styles.sass";

.listing-card-container {
    .v-card__title {
        word-break: normal;
    }
    .no-border-radius {
        &.v-chip--label {
            border-radius: 0 !important;
        }
    }
    .card-header {
        position: relative;
        z-index: 3;
    }
    .scheme-ribbon,
    .secondary-label {
        position: absolute;
        right: -6px;
        top: 4px;
        width: auto;
        z-index: 10;
        padding: 5px 10px;
        background-color: #0b0c0c;
        span {
            font-size: 12px;
            font-weight: 700;
            color: #fff;
            &:after {
                content: "";
                position: absolute;
                right: 0px;
                bottom: -6px;
                width: 6px;
                height: 6px;
                background: linear-gradient(
                        0deg,
                        rgba(0, 0, 0, 0.4),
                        rgba(0, 0, 0, 0.4)
                    ),
                    rgb(255, 79, 53);
                clip-path: polygon(0px 0px, 0% 100%, 100% 0px);
            }
        }
    }

    .type-ribbon {
        position: absolute;
        left: 0px;
        top: 4px;
        width: auto;
        z-index: 4;
        border-radius: 0px !important;
        padding: 5px 10px;
        &.resale {
            background-color: var(--v-secondary-base) !important;
        }
        &.rental {
            background-color: var(--v-accent-base) !important;
        }
    }

    .listing-card {
        .col {
            padding-top: 0px;
            padding-bottom: 0px;
        }

        .hp {
            position: relative;
            height: 65px;
            margin-top: 15px;
            margin-bottom: 10px;
            padding: 8px 0px;
            text-align: center;
            background: #fff;

            &:before {
                position: absolute;
                content: "";
                width: 30px;
                top: 0;
                right: 100%;
                bottom: 0;
            }

            img {
                display: block;
                margin: 0 auto;
                max-height: 100%;
            }

            @media #{map-get($display-breakpoints, 'xs-only')} {
                margin-top: 0px;
                margin-bottom: 0px;
            }
        }

        .card-header {
            border: 0px;

            .v-carousel {
                height: inherit !important;

                .v-responsive__content {
                    height: inherit;

                    > a {
                        height: inherit;
                    }
                }
            }

            > a {
                height: inherit;
            }

            .v-image {
                height: inherit !important;
            }
        }

        .card-body-outer {
            position: relative;
            width: 100%;

            > a {
                position: absolute;
                display: block;
                top: 0;
                bottom: 0;
                text-decoration: none;
                color: unset;
            }

            .card-body {
                padding-right: 30px;
                font-size: 17px;
                line-height: 22px;

                .v-card__title {
                    font-size: 22px;
                    line-height: 28px;
                }

                div {
                    font-size: inherit;
                }

                @media #{map-get($display-breakpoints, 'md-and-down')} {
                    font-size: 15px;
                }

                @media #{map-get($display-breakpoints, 'xs-only')} {
                    padding: 10px 15px;
                }
            }
        }

        &-featured {
            background-color: var(--v-primary-base);

            .card-body-outer {
                .card-body {
                    color: #fff;
                }
            }
        }
        &-highlighted {
            background-color: var(--v-primary-base);

            .card-body-outer {
                .card-body {
                    color: #fff;
                }
            }
        }

        &.listing-card-landscape {
            .card-header {
                height: 100%;
                min-height: 300px;
                max-height: 380px;
            }

            .card-body-outer {
                > a {
                    left: -12px;
                    right: 12px;
                }

                .card-body {
                    padding-left: 0px;
                }
            }

            .hp {
                margin-left: -24px;
            }
        }

        &.listing-card-portrait {
            .card-header {
                height: 200px;
            }

            .card-body-outer {
                > a {
                    left: 12px;
                    right: 12px;
                }

                .card-body {
                    padding-left: 30px;

                    .v-card__title {
                        padding-top: 0px;
                    }
                }
            }
        }
    }
}
</style>
